import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Search,
  MessageSquare,
  FileCheck,
  CreditCard,
  Ship,
  CheckCircle,
  Clock,
  Shield,
  HeadphonesIcon
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';

const HowItWorksPage = () => {
  const [currency, setCurrency] = useState('USD');

  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Browse & Select',
      description: 'Explore our inventory of quality Japanese vehicles',
      details: [
        'Use advanced filters to find your perfect vehicle',
        'View detailed specifications and photos',
        'Check vehicle condition reports',
        'Compare multiple vehicles'
      ],
      timeframe: '10-30 minutes'
    },
    {
      number: '02',
      icon: MessageSquare,
      title: 'Inquire & Quote',
      description: 'Get detailed pricing and information',
      details: [
        'Submit inquiry form or contact us directly',
        'Receive comprehensive quotation within 24 hours',
        'Get shipping estimate to your destination',
        'Ask questions and clarify any concerns'
      ],
      timeframe: '24 hours'
    },
    {
      number: '03',
      icon: FileCheck,
      title: 'Confirm & Documentation',
      description: 'Review and finalize your purchase',
      details: [
        'Receive proforma invoice with all details',
        'Review terms and conditions',
        'Confirm vehicle reservation',
        'Receive payment instructions'
      ],
      timeframe: '1-2 days'
    },
    {
      number: '04',
      icon: CreditCard,
      title: 'Make Payment',
      description: 'Secure payment processing',
      details: [
        'Pay via international bank transfer',
        'Payment confirmation within 24 hours',
        'Receive official payment receipt',
        'Vehicle secured for export'
      ],
      timeframe: '1-3 days'
    },
    {
      number: '05',
      icon: Ship,
      title: 'Export & Shipping',
      description: 'Vehicle preparation and shipping',
      details: [
        'Vehicle inspection and preparation',
        'Export documentation processing',
        'Loading arrangement and scheduling',
        'Tracking information provided'
      ],
      timeframe: '2-8 weeks'
    },
    {
      number: '06',
      icon: CheckCircle,
      title: 'Receive Vehicle',
      description: 'Customs clearance and delivery',
      details: [
        'Receive all original export documents',
        'Clear customs at destination port',
        'Arrange vehicle pickup',
        'Complete registration in your country'
      ],
      timeframe: 'Varies by location'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Quality Guaranteed',
      description: 'All vehicles inspected and verified before export'
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Quick turnaround from purchase to shipment'
    },
    {
      icon: HeadphonesIcon,
      title: '24/7 Support',
      description: 'Dedicated team to assist you at every step'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="How It Works - Global Trade Car | Vehicle Buying Guide"
        description="Learn how to buy Japanese vehicles from Global Trade Car. Simple 6-step process from browsing to delivery."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            How It Works
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Buying a Japanese vehicle is simple with Global Trade Car. Follow our straightforward 6-step process from selection to delivery.
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-16">
          <div className="space-y-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}>
                  {/* Content Card */}
                  <div className="flex-1 w-full">
                    <Card className="hover:shadow-xl transition-shadow">
                      <CardContent className="p-8">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Icon className="h-8 w-8 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm text-red-600 font-semibold mb-1">Step {step.number}</div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                            <p className="text-gray-600 mb-4">{step.description}</p>
                          </div>
                        </div>
                        
                        <ul className="space-y-2 mb-4">
                          {step.details.map((detail, idx) => (
                            <li key={`detail-${idx}`} className="flex items-start space-x-2">
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{detail}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="flex items-center space-x-2 text-sm text-gray-500 mt-4 pt-4 border-t">
                          <Clock className="h-4 w-4" />
                          <span>Timeframe: {step.timeframe}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Step Number (Large) */}
                  <div className="flex-shrink-0 hidden lg:block">
                    <div className="w-32 h-32 rounded-full border-4 border-red-600 flex items-center justify-center">
                      <span className="text-5xl font-bold text-red-600">{step.number}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Total Timeline */}
        <div className="mb-16">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Total Process Time</h3>
              <p className="text-4xl font-bold text-red-600 mb-2">3-10 Weeks</p>
              <p className="text-gray-600">From inquiry to receiving your vehicle (varies by destination)</p>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Buy From Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                      <Icon className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Common Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How long does the entire process take?</h3>
                <p className="text-sm text-gray-600">From inquiry to receiving your vehicle, the process typically takes 3-10 weeks, depending on your location and shipping schedules.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What payment methods do you accept?</h3>
                <p className="text-sm text-gray-600">We accept international bank transfers (TT/Wire transfer). Payment must be made before the vehicle is shipped.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I get a vehicle inspection?</h3>
                <p className="text-sm text-gray-600">Yes! All vehicles come with standard inspection reports. You can also request additional detailed inspections or third-party inspections.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Do you handle shipping arrangements?</h3>
                <p className="text-sm text-gray-600">Absolutely! We arrange all shipping logistics from Japan to your destination port and provide all necessary export documentation.</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-8">
            <a href="/#faq" className="text-red-600 hover:text-red-700 font-semibold">
              View All FAQs →
            </a>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Browse our inventory or contact us to begin your vehicle purchase journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/vehicles">
              <button className="px-8 py-4 bg-white text-red-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold">
                Browse Vehicles
              </button>
            </a>
            <a href="/contact">
              <button className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-red-700 transition-colors font-semibold">
                Contact Us
              </button>
            </a>
          </div>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default HowItWorksPage;
